/**
 * Pumpkins — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pumpkinsPack: ToilePack = {
    slug: 'pumpkins',
    category: 'motif',
    name: 'Pumpkins',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'jack', label: "Jack-o'-lantern", subject: 'A carved pumpkin with a grinning face lit from within by a candle, the light glowing through the eyes and teeth, the stem curling, on a wooden step at night.'},
        {key: 'patch', label: 'Pumpkin patch', subject: 'A pumpkin patch with a dozen orange pumpkins among broad leaves and curling vines, a wooden wheelbarrow, a scarecrow, a barn and a low autumn sun.'},
        {key: 'stack', label: 'Porch stack', subject: 'A stack of heirloom pumpkins on a porch step — a pale blue-grey one, a white one, a warty orange one, a small striped gourd — with a wreath of dried leaves and a lantern.'},
    ],
    styles: [
        {key: 'vintage', label: 'Vintage', style: 'vintage autumn illustration, warm painted, soft grain, cream paper', palette: 'warm orange, brown and cream with a soft painted grain', framing: 'centred on a cream paper background with a soft painted vignette'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii illustration, round shapes, a tiny smiling face, pastel, thick outlines', palette: 'pastel orange and cream with a tiny smiling face', framing: 'centred on a soft pastel background with a thick white outline'},
        {key: 'line', label: 'Line drawing', style: 'single-weight black line drawing, clean contour, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a wide even margin'},
    ],
    directions: ['a white pumpkin instead', 'carved with a cat face', 'for a fall wreath'],
    directionHint: 'try: a white pumpkin instead',
    seo: {
        title: "Pumpkin art: jack-o'-lantern, patch, porch stack",
        metaDescription: "Nine pumpkin illustrations — carved jack-o'-lantern, patch and porch stack — in vintage, kawaii and line-drawing styles for Halloween and fall.",
        h1: "Pumpkin drawings: jack-o'-lantern, patch, porch stack in three styles",
        tagline: 'Nine renders, three pumpkin readings, three looks each — carved to line.',
        intro: "October's motif drawn three ways: a carved jack-o'-lantern lit from within, a pumpkin patch thick with vines, and a porch stack of heirloom pumpkins. Each renders in vintage painted, kawaii and clean line styles. Ask for a white pumpkin or a cat-face carving and the same three looks carry it.",
        useCases: [
            'a Halloween or fall card',
            'a classroom October bulletin board',
            'a porch or kitchen print',
            'a social media autumn post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a tall portrait crop, suited to a card, a poster or a print.'},
            {q: 'Can I get a different pumpkin, like a white one or a cat-face carving?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Vintage carries a warm painted autumn grain, kawaii adds a round smiling face in pastel, and line drawing is a single black contour with no colour.'},
            {q: 'Is this a real photograph of a pumpkin?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
